import React, { useState } from 'react';
import { EyeIcon } from '@heroicons/react/24/solid';
import { TAILWIND_COLORS } from '../components/ColorPicker'; 
import { formatSportDisplayName } from '../utils/sportName';

const PermissionsModal = ({
  isOpen,
  onClose,
  selectedUserType,
  onUpdateUserType,
  inputClass, // Pass the common input class from parent
  categories,
  bookingOptions,
  onPreviewBooking,
  canPreviewBooking = true
}) => {
  const [modalErrors, setModalErrors] = useState({});

  const handleModalPermissionChange = (field, value) => {
    if (!selectedUserType) return;
    const currentPermissions = selectedUserType.permissions[selectedUserType.sport] || {};
    const nextPermissions = {
      ...currentPermissions,
      [field]: value
    };
    delete nextPermissions.canBookLights;
    delete nextPermissions.feePerLightsToken;

    const updatedUserType = {
      ...selectedUserType,
      permissions: {
        ...selectedUserType.permissions,
        [selectedUserType.sport]: nextPermissions
      }
    };
    console.log('[PermissionsModal] handleModalPermissionChange', { field, value, updatedUserType });
    onUpdateUserType(updatedUserType);
  };

  if (!isOpen || !selectedUserType) return null;

  const rawBookings = selectedUserType.permissions[selectedUserType.sport] || {
    canBook: false,
    feePerSlot: 0,
    canOperateLights: false,
    lightsFeePerMinute: 0,
    canBookExtras: false,
    bookingExtras: [],
    canBookMultipleCourts: false,
    canBookRecurring: [],
    advanceBookingLimit: -1,
    cancellationCutoffHours: 0,
    bookingCategories: [],
    canMakePublicBookings: false
  };
  const bookings = {
    ...rawBookings,
    canOperateLights:
      rawBookings.canOperateLights ?? rawBookings.canBookLights ?? false,
    lightsFeePerMinute:
      rawBookings.lightsFeePerMinute ?? rawBookings.feePerLightsToken ?? 0,
    canBookExtras: rawBookings.canBookExtras ?? false,
    bookingExtras: Array.isArray(rawBookings.bookingExtras) ? rawBookings.bookingExtras : [],
    cancellationCutoffHours: Number.isFinite(rawBookings.cancellationCutoffHours)
      ? rawBookings.cancellationCutoffHours
      : 0
  };
  const canBookRecurring = bookings.canBookRecurring || [];
  const sportOptions = (bookingOptions || []).filter(option =>
    option.option?.startsWith(`${selectedUserType.sport}-`)
  );
  const validSportOptionIds = new Set(sportOptions.map(option => option.option));
  const bookingExtras = (bookings.bookingExtras || []).filter(extra =>
    validSportOptionIds.has(extra?.option)
  );

  const handleExtrasEnabledChange = (enabled) => {
    const updatedUserType = {
      ...selectedUserType,
      permissions: {
        ...selectedUserType.permissions,
        [selectedUserType.sport]: {
          ...bookings,
          canBookExtras: enabled,
          bookingExtras: enabled ? bookingExtras : []
        }
      }
    };
    onUpdateUserType(updatedUserType);
  };

  const handleExtraToggle = (optionId, checked) => {
    const updatedExtras = checked
      ? [...bookingExtras.filter(extra => extra.option !== optionId), { option: optionId, fee: 0 }]
      : bookingExtras.filter(extra => extra.option !== optionId);

    const updatedUserType = {
      ...selectedUserType,
      permissions: {
        ...selectedUserType.permissions,
        [selectedUserType.sport]: {
          ...bookings,
          bookingExtras: updatedExtras
        }
      }
    };
    onUpdateUserType(updatedUserType);
  };

  const handleExtraFeeChange = (optionId, value) => {
    const normalizedValue = Number.isNaN(value) ? 0 : value;
    const updatedExtras = bookingExtras.map(extra =>
      extra.option === optionId ? { ...extra, fee: normalizedValue } : extra
    );
    const updatedUserType = {
      ...selectedUserType,
      permissions: {
        ...selectedUserType.permissions,
        [selectedUserType.sport]: {
          ...bookings,
          bookingExtras: updatedExtras
        }
      }
    };
    onUpdateUserType(updatedUserType);
  };

  // Helper: check if a frequency is enabled
  const isFrequencyEnabled = (freq) => canBookRecurring.some(r => r.frequency === freq);
  // Helper: get duration for a frequency
  const getDuration = (freq) => canBookRecurring.find(r => r.frequency === freq)?.duration || 2;
  // Helper: get block status for a frequency
  const getBlock = (freq) => canBookRecurring.find(r => r.frequency === freq)?.block || false;

  // Handler: toggle recurring enabled
  const handleRecurringEnabled = (enabled) => {
    let updatedRecurring = enabled
      ? [
          ...(isFrequencyEnabled('daily') ? [canBookRecurring.find(r => r.frequency === 'daily')] : [{ frequency: 'daily', duration: 2, block: false }]),
          ...(isFrequencyEnabled('weekly') ? [canBookRecurring.find(r => r.frequency === 'weekly')] : [{ frequency: 'weekly', duration: 2, block: false }])
        ]
      : [];
    const updatedUserType = {
      ...selectedUserType,
      permissions: {
        ...selectedUserType.permissions,
        [selectedUserType.sport]: {
          ...bookings,
          canBookRecurring: updatedRecurring
        }
      }
    };
    console.log('[PermissionsModal] handleRecurringEnabled', { enabled, updatedUserType });
    onUpdateUserType(updatedUserType);
  };

  // Handler: toggle frequency
  const handleFrequencyChange = (freq, checked) => {
    let updatedRecurring = canBookRecurring.filter(r => r.frequency !== freq);
    if (checked) {
      // Preserve existing block status if frequency was previously enabled, otherwise default to false
      const existing = canBookRecurring.find(r => r.frequency === freq);
      updatedRecurring = [...updatedRecurring, { 
        frequency: freq, 
        duration: existing?.duration || 2,
        block: existing?.block || false
      }];
    }
    const updatedUserType = {
      ...selectedUserType,
      permissions: {
        ...selectedUserType.permissions,
        [selectedUserType.sport]: {
          ...bookings,
          canBookRecurring: updatedRecurring
        }
      }
    };
    console.log('[PermissionsModal] handleFrequencyChange', { freq, checked, updatedUserType });
    onUpdateUserType(updatedUserType);
  };

  // Handler: change duration
  const handleDurationChange = (freq, value) => {
    const updatedRecurring = canBookRecurring.map(r =>
      r.frequency === freq ? { ...r, duration: parseInt(value) || 2 } : r
    );
    const updatedUserType = {
      ...selectedUserType,
      permissions: {
        ...selectedUserType.permissions,
        [selectedUserType.sport]: {
          ...bookings,
          canBookRecurring: updatedRecurring
        }
      }
    };
    console.log('[PermissionsModal] handleDurationChange', { freq, value, updatedUserType });
    onUpdateUserType(updatedUserType);
  };

  // Handler: toggle block (Never Ending)
  const handleBlockChange = (freq, checked) => {
    const updatedRecurring = canBookRecurring.map(r =>
      r.frequency === freq ? { ...r, block: checked } : r
    );
    const updatedUserType = {
      ...selectedUserType,
      permissions: {
        ...selectedUserType.permissions,
        [selectedUserType.sport]: {
          ...bookings,
          canBookRecurring: updatedRecurring
        }
      }
    };
    console.log('[PermissionsModal] handleBlockChange', { freq, checked, updatedUserType });
    onUpdateUserType(updatedUserType);
  };

  // Validation
  const validatePermissionsModal = () => {
    const errors = {};
    if (bookings.canBook) {
      if (bookings.advanceBookingLimit === '' || isNaN(bookings.advanceBookingLimit)) {
        errors.advanceBookingLimit = 'Required, set to -1 for no limit';
      }
      if (bookings.feePerSlot === '' || isNaN(bookings.feePerSlot)) {
        errors.feePerSlot = 'Fee per Slot is required';
      }
      if (bookings.canOperateLights && (bookings.lightsFeePerMinute === '' || isNaN(bookings.lightsFeePerMinute))) {
        errors.lightsFeePerMinute = 'Fee per minute is required';
      }
      if (canBookRecurring.length > 0) {
        if (!isFrequencyEnabled('daily') && !isFrequencyEnabled('weekly')) {
          errors.frequency = 'Select at least one frequency option (Daily or Weekly)';
        }
        if (isFrequencyEnabled('daily')) {
          const val = getDuration('daily');
          if (val === '' || isNaN(val) || val < 2) {
            errors.dailyDuration = 'Daily duration must be at least 2 days';
          }
        }
        if (isFrequencyEnabled('weekly')) {
          const val = getDuration('weekly');
          if (val === '' || isNaN(val) || val < 2) {
            errors.weeklyDuration = 'Weekly duration must be at least 2 weeks';
          }
        }
      }
      
      // New validation for booking categories
      if (!bookings.bookingCategories || bookings.bookingCategories.length === 0) {
        errors.bookingCategories = 'At least one booking category must be enabled';
      }
    }
    setModalErrors(errors);
    console.log('[PermissionsModal] validatePermissionsModal', { errors });
    return Object.keys(errors).length === 0;
  };

  const handleCloseModal = () => {
    const valid = validatePermissionsModal();
    console.log('[PermissionsModal] handleCloseModal', { valid, modalErrors });
    if (valid) {
      setModalErrors({});
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto overscroll-contain bg-slate-500/75"
      role="dialog"
      aria-modal="true"
      aria-labelledby="permissions-modal-title"
    >
      {/* Scroll the overlay (not the page behind) so tall content stays reachable on short viewports */}
      <div className="flex min-h-screen w-full items-center justify-center p-3 py-8 sm:p-4 sm:py-10">
        <div className="relative w-full max-w-2xl bg-white rounded-lg shadow-xl">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 id="permissions-modal-title" className="text-lg font-medium text-slate-800 pr-2">
              {formatSportDisplayName(selectedUserType.sport)} Booking Permissions for {selectedUserType.name}
            </h3>
            <button
              onClick={onClose}
              className="text-slate-600 hover:text-slate-600"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-6">

            {/* Can Book Courts with Fee */}
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={bookings.canBook}
                onChange={(e) => handleModalPermissionChange('canBook', e.target.checked)}
                className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-slate-600">Can Book Courts</span>
            </label>

            {bookings.canBook && (
              <div className="ml-6 space-y-4">
                <div className="grid grid-cols-2 gap-4 items-center">
                  <label className="block text-sm text-slate-600">Advance Booking Limit (in days)</label>
                  <div>
                    <input
                      type="number"
                      value={bookings.advanceBookingLimit}
                      onChange={(e) => handleModalPermissionChange('advanceBookingLimit', parseFloat(e.target.value))}
                      className={`${inputClass} ${modalErrors.advanceBookingLimit ? 'border-red-500' : ''}`}
                      min="-1"
                    />
                    {modalErrors.advanceBookingLimit && (
                      <p className="mt-1 text-xs text-red-500">{modalErrors.advanceBookingLimit}</p>
                    )}
                  </div>
                  <label className="block text-sm text-slate-600">Cancellation cutoff (hours before start)</label>
                  <div>
                    <input
                      type="number"
                      value={bookings.cancellationCutoffHours}
                      onChange={(e) => handleModalPermissionChange('cancellationCutoffHours', parseFloat(e.target.value))}
                      className={inputClass}
                      min="0"
                    />
                  </div>
                  <label className="block text-sm text-slate-600">Base Fee Per Slot</label>
                  <div>
                    <input
                      type="number"
                      value={bookings.feePerSlot}
                      onChange={(e) => handleModalPermissionChange('feePerSlot', parseFloat(e.target.value))}
                      className={`${inputClass} ${modalErrors.feePerSlot ? 'border-red-500' : ''}`}
                      min="0"
                    />
                    {modalErrors.feePerSlot && (
                      <p className="mt-1 text-xs text-red-500">{modalErrors.feePerSlot}</p>
                    )}
                  </div>
                </div>

                {/* Floodlight Settings */}
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={bookings.canOperateLights}
                    onChange={(e) => handleModalPermissionChange('canOperateLights', e.target.checked)}
                    className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-slate-600">Can Operate Floodlights</span>
                </label>

                {bookings.canOperateLights && (
                  <div className="grid grid-cols-2 gap-4 items-center ml-6">
                    <label className="block text-sm text-slate-600">Fee Per Minute Per Court</label>
                    <div>
                      <input
                        type="number"
                        value={bookings.lightsFeePerMinute}
                        onChange={(e) => handleModalPermissionChange('lightsFeePerMinute', parseFloat(e.target.value))}
                        className={`${inputClass} ${modalErrors.lightsFeePerMinute ? 'border-red-500' : ''}`}
                        min="0"
                      />
                      {modalErrors.lightsFeePerMinute && (
                        <p className="mt-1 text-xs text-red-500">{modalErrors.lightsFeePerMinute}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Booking Extras */}
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={bookings.canBookExtras}
                    onChange={(e) => handleExtrasEnabledChange(e.target.checked)}
                    className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-slate-600">Can Book Extras</span>
                </label>

                {bookings.canBookExtras && (
                  <div className="ml-6 space-y-2">
                    {sportOptions.length === 0 ? (
                      <p className="text-xs text-slate-600">
                        No Booking Extras are configured for {selectedUserType.sport}
                      </p>
                    ) : (
                      sportOptions.map((option) => {
                        const isSelected = bookingExtras.some(extra => extra.option === option.option);
                        const fee = bookingExtras.find(extra => extra.option === option.option)?.fee ?? 0;
                        return (
                          <div key={option.option} className="flex items-center gap-3">
                            <label className="flex items-center flex-1 min-w-0">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => handleExtraToggle(option.option, e.target.checked)}
                                className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="ml-2 text-sm text-slate-600 truncate">
                                {option.name || option.option}
                              </span>
                            </label>
                            {isSelected && (
                              <input
                                type="number"
                                value={fee}
                                onChange={(e) => handleExtraFeeChange(option.option, parseFloat(e.target.value))}
                                className={`${inputClass} max-w-[140px]`}
                                min="0"
                                placeholder="Fee"
                              />
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                )}

                {/* Multiple Courts */}
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={bookings.canBookMultipleCourts}
                    onChange={(e) => handleModalPermissionChange('canBookMultipleCourts', e.target.checked)}
                    className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-slate-600">Can Book Multiple Courts In Single Booking</span>
                </label>

                {/* Public Bookings */}
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={bookings.canMakePublicBookings || false}
                    onChange={(e) => handleModalPermissionChange('canMakePublicBookings', e.target.checked)}
                    className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-slate-600">Can Make Public Bookings</span>
                </label>

                {/* Recurring Bookings */}
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={canBookRecurring.length > 0}
                    onChange={(e) => handleRecurringEnabled(e.target.checked)}
                    className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-slate-600">Can Make Recurring Bookings</span>
                </label>

                {canBookRecurring.length > 0 && (
                  <div className="ml-6 space-y-4">
                    {modalErrors.frequency && (
                      <p className="text-xs text-red-500">{modalErrors.frequency}</p>
                    )}
                    <div className="grid grid-cols-2 gap-8">
                      <div className="space-y-2">
                        <div className="flex items-center gap-4">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={isFrequencyEnabled('daily')}
                              onChange={(e) => handleFrequencyChange('daily', e.target.checked)}
                              className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="ml-2 text-sm text-slate-600">Daily</span>
                          </label>
                          {isFrequencyEnabled('daily') && (
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={getBlock('daily')}
                                onChange={(e) => handleBlockChange('daily', e.target.checked)}
                                className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="ml-2 text-xs text-slate-600">Never Ending</span>
                            </label>
                          )}
                        </div>
                        {isFrequencyEnabled('daily') && (
                          <div className="ml-6">
                            <label className="block text-xs text-slate-600 mb-1">Maximum days</label>
                            <input
                              type="number"
                              value={getDuration('daily')}
                              onChange={(e) => handleDurationChange('daily', e.target.value)}
                              className={`${inputClass} ${modalErrors.dailyDuration ? 'border-red-500' : ''}`}
                              min="2"
                              max="14"
                            />
                            {modalErrors.dailyDuration && (
                              <p className="mt-1 text-xs text-red-500">{modalErrors.dailyDuration}</p>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center gap-4">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={isFrequencyEnabled('weekly')}
                              onChange={(e) => handleFrequencyChange('weekly', e.target.checked)}
                              className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="ml-2 text-sm text-slate-600">Weekly</span>
                          </label>
                          {isFrequencyEnabled('weekly') && (
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={getBlock('weekly')}
                                onChange={(e) => handleBlockChange('weekly', e.target.checked)}
                                className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="ml-2 text-xs text-slate-600">Never Ending</span>
                            </label>
                          )}
                        </div>
                        {isFrequencyEnabled('weekly') && (
                          <div className="ml-6">
                            <label className="block text-xs text-slate-600 mb-1">Maximum weeks</label>
                            <input
                              type="number"
                              value={getDuration('weekly')}
                              onChange={(e) => handleDurationChange('weekly', e.target.value)}
                              className={`${inputClass} ${modalErrors.weeklyDuration ? 'border-red-500' : ''}`}
                              min="2"
                              max="12"
                            />
                            {modalErrors.weeklyDuration && (
                              <p className="mt-1 text-xs text-red-500">{modalErrors.weeklyDuration}</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Booking Categories Section */}
          {bookings.canBook && (
            <div className="mt-6 space-y-4">
              <h4 className="text-sm font-medium text-slate-800">Booking Categories</h4>
              {/* Enabled Categories */}
              <div>
                <label className="block text-xs text-slate-600 mb-2">Enabled Categories</label>
                <div className="flex flex-wrap gap-2">
                  {(bookings.bookingCategories || []).map(categoryId => {
                    const category = categories.find(c => c.id === categoryId && c.type === selectedUserType.sport);
                    if (!category) return null;
                    return (
                      <button
                        key={categoryId}
                        onClick={() => {
                          const updatedCategories = bookings.bookingCategories.filter(id => id !== categoryId);
                          handleModalPermissionChange('bookingCategories', updatedCategories);
                        }}
                        className={`inline-flex items-center justify-center px-3 py-1 text-xs font-medium rounded-md border cursor-pointer
                          ${TAILWIND_COLORS[category.colour].bg} 
                          ${TAILWIND_COLORS[category.colour].text} 
                          ${TAILWIND_COLORS[category.colour].border}
                          hover:opacity-80 transition-opacity`}
                      >
                        {category.name}
                      </button>
                    );
                  })}
                </div>
                {modalErrors.bookingCategories && (
                  <p className="mt-1 text-xs text-red-500">{modalErrors.bookingCategories}</p>
                )}
              </div>

              {/* Available Categories */}
              <div>
                <label className="block text-xs text-slate-600 mb-2">Available Categories</label>
                <div className="flex flex-wrap gap-2">
                  {categories
                    .filter(category => 
                      category.type === selectedUserType.sport && 
                      (!bookings.bookingCategories || !bookings.bookingCategories.includes(category.id)) &&
                      category.name !== 'Available' && 
                      category.name !== 'Recurring'
                    )
                    .map(category => (
                      <button
                        key={category.id}
                        onClick={() => {
                          const updatedCategories = [...(bookings.bookingCategories || []), category.id];
                          handleModalPermissionChange('bookingCategories', updatedCategories);
                        }}
                        className={`inline-flex items-center justify-center px-3 py-1 text-xs font-medium rounded-md border cursor-pointer
                          ${TAILWIND_COLORS[category.colour].bg} 
                          ${TAILWIND_COLORS[category.colour].text} 
                          ${TAILWIND_COLORS[category.colour].border}
                          hover:opacity-80 transition-opacity`}
                      >
                        {category.name}
                      </button>
                    ))}
                </div>
              </div>
            </div>
          )}

          <div className="mt-6 flex flex-wrap justify-end gap-3">
            {typeof onPreviewBooking === 'function' && (
              <button
                type="button"
                onClick={onPreviewBooking}
                disabled={!canPreviewBooking}
                title={canPreviewBooking ? 'See the booking form as this user type would' : 'Add a court for this sport under Court Details first'}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
              >
                <EyeIcon className="h-4 w-4" />
                Preview
              </button>
            )}
            <button
              type="button"
              onClick={handleCloseModal}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Done
            </button>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default PermissionsModal; 